<script>
    import {
        Chart as ChartJS,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        BarElement,
        Title,
        Tooltip,
        Legend,
        Colors,
        Filler,
    } from 'chart.js';
    import { Bar, Line } from 'vue-chartjs';

    import ChartDataLabels from 'chartjs-plugin-datalabels';

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        BarElement,
        Title,
        Tooltip,
        Legend,
        Colors,
        Filler,
        ChartDataLabels,
    );

    // Import general mixin for shared functionality.
    import mixin from '../../mixin';

    export default {
        mixins: [mixin],
        components: {
            Bar,
            Line,
        },
        props: {
            data: Object,
        },
        data() {
            return {
                facetAliasHeadsum: 'hoofdsom_totaal',
                facetAliasYear: 'ingangsjaar',
                options: {
                    locale: 'nl-NL',
                    maintainAspectRatio: false,
                    scales: {
                        'left-y-axis': {
                            type: 'linear',
                            position: 'left',
                            title: {
                                display: true,
                                text: this.$store.getters.getText('dashboardWidgetAmount'),
                            },
                            grid: {
                                display: false
                            },
                        },
                        'right-y-axis': {
                            type: 'linear',
                            position: 'right',
                            title: {
                                display: true,
                                text: this.$store.getters.getText('dashboardWidgetSumsHeadsum'),
                            },
                            ticks: {
                                callback: (value) => this.toEuro(value, true),
                            },
                            grid: {
                                display: false
                            },
                        },
                        x: {
                            grid: {
                                display: false
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                        title: {
                            display: true,
                            text: this.$store.getters.getText('dashboardWidgetAmountheadsumyear'),
                            padding: {
                                top: 10,
                                right: 10,
                                bottom: 20,
                                left: 10,
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    if (context.dataset.label === this.$store.getters.getText('dashboardWidgetSumsHeadsum')) {
                                        return `${context.dataset.label}: ${this.toEuro(context.parsed.y, true)}`;
                                    }
                                    else {
                                        return `${context.dataset.label}: ${context.parsed.y}`;
                                    }
                                },
                            },
                        },
                    }
                },
            };
        },
        computed: {
            facetsSet() {
                return this.data && this.data.facets && this.data.facets.length;
            },
            years() {
                if (!this.facetsSet) return [];
                const facet = this.data.facets.find(f => f.alias === this.facetAliasYear);
                if (!facet || !facet.options || !facet.options.length) return [];
                let options = facet.options.filter(option => option.filteredAmount);
                if (!options.length) return [];
                options = options.sort((a, b) => parseInt(a.valueAlias) - parseInt(b.valueAlias));
                const firstYear = parseInt(facet.options[0].valueAlias);
                const lastYear = parseInt(facet.options[facet.options.length - 1].valueAlias);
                if (firstYear === lastYear) return [firstYear];
                const years = [];
                for (let i = firstYear; i <= lastYear; i++) {
                    years.push(i);
                }
                return years;
            },
            dataSets() {
                const defaultValue = { labels: [], datasets: [] };
                if (!this.years.length) return defaultValue;
                const facet = this.data.facets.find(f => f.alias === this.facetAliasYear);
                if (!facet || !facet.options || !facet.options.length) return defaultValue;

                const barData = this.years.map(year => {
                    const option = facet.options.find(o => parseInt(o.valueAlias) === year);
                    return option ? option.filteredAmount : 0;
                });

                const lineData = this.years.map(year => {
                    const option = facet.options.find(o => parseInt(o.valueAlias) === year);
                    return option ? option.sums[this.facetAliasHeadsum] : 0;
                });

                return {
                    labels: this.years,
                    datasets: [
                        {
                            type: 'line',
                            yAxisID: 'right-y-axis',
                            label: this.$store.getters.getText('dashboardWidgetSumsHeadsum'),
                            data: lineData,
                            datalabels: {
                                formatter: (value, ctx) => this.toEuro(value, true),
                                display: 'auto',
                                anchor: 'center',
                                align: 'top',
                                clamp: true,
                                font: {
                                    lineHeight: 1,
                                },
                                color: '#000000',
                            },
                            backgroundColor: this.$store.getters.getHighContrast ? '#53b7d8' : '#e16032',
                            borderColor: this.$store.getters.getHighContrast ? '#53b7d8' : '#e16032',
                        },
                        {
                            yAxisID: 'left-y-axis',
                            label: this.$store.getters.getText('dashboardWidgetAmount'),
                            data: barData,
                            datalabels: {
                                display: 'auto',
                                anchor: 'end',
                                align: 'bottom',
                                clamp: true,
                                font: {
                                    lineHeight: 1,
                                },
                                color: this.$store.getters.getHighContrast ? '#ffffff' : '#000000',
                            },
                            backgroundColor: this.$store.getters.getHighContrast ? '#833de6' : '#53b7d8',
                        },
                    ],
                };
            },
        },
    };
</script>

<template>
    <div class="dashboard-widget dashboard-widget--minheight">
        <Bar :data="dataSets" :options="options" />
    </div>
</template>
